import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSidebar } from '@context/SidebarContext'

import { Body2 } from '@ui/Typography'

import { isMenuItemActive, stripLocale } from '@shared/components/Navigation/v2/Sidebar/utils'

import { MenuLink } from '../../MenuLink'
import { MenuSection, MenuVariant } from '../../menuSections/useMenuSections'

import styles from './SidebarSubmenu.module.scss'

interface SidebarSubmenuProps {
  parentItem: MenuSection
}

export const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({ parentItem }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const {
    closeSubmenu,
    submenuTop,
    adjustSubmenuPosition,
    setSubmenuElement,
    cancelPendingSubmenuChange,
  } = useSidebar()
  const submenuRef = useRef<HTMLDivElement>(null)

  const children = parentItem.children?.filter((child) => child.condition !== false) ?? []

  const handleSubmenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      submenuRef.current = node
      setSubmenuElement(node)
    },
    [setSubmenuElement]
  )

  useLayoutEffect(() => {
    if (submenuRef.current) {
      const submenuHeight = submenuRef.current.offsetHeight
      adjustSubmenuPosition(submenuHeight, submenuTop)
    }
  }, [adjustSubmenuPosition, submenuTop])

  const handleItemClick = useCallback(
    (item: MenuSection) => {
      if (item.onClick) {
        item.onClick()
      } else if (item.path) {
        navigate(item.path)
      }
      closeSubmenu()
    },
    [navigate, closeSubmenu]
  )

  if (children.length === 0) {
    return null
  }

  const isActionMenu = parentItem.variant === MenuVariant.ACTION
  const current = stripLocale(pathname)

  return (
    <AnimatePresence>
      <motion.div
        ref={handleSubmenuRef}
        onMouseEnter={cancelPendingSubmenuChange}
        className={styles.submenu}
        style={{ top: submenuTop }}
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -12 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        <Body2 medium className={styles.submenuTitle}>
          {parentItem.label}
        </Body2>

        {children.map((child) => {
          const isActive = isMenuItemActive(current, child.path, isActionMenu)

          return (
            <React.Fragment key={child.id}>
              <MenuLink
                section={child}
                isActive={isActive}
                chevronDirection='right'
                onClick={() => handleItemClick(child)}
                className={styles.submenuItem}
              />
            </React.Fragment>
          )
        })}
      </motion.div>
    </AnimatePresence>
  )
}
